import type { EmailLink } from "@dashboardApi/apis/engagementLinkAliases";

import type { FetchRequestError } from "@lib/errors/FetchRequestError";
import { FetchUserEncodedError } from "@lib/errors/FetchUserEncodedError";
import type { MultiLanguageCompositionSetup } from "@lib/messageComposition";
import type { SHARED_CONSTANTS_LIQUID_CONSTANTS } from "@lib/shared_constants";

export type ContentBlockInvalidReferenceError = FetchUserEncodedError<
  SHARED_CONSTANTS_LIQUID_CONSTANTS["CONTENT_BLOCKS"]["ERROR_CODES"]["INVALID_CONTENT_BLOCK_REFERENCE"]
>;

export type ContentBlockNotFoundError = FetchUserEncodedError<
  SHARED_CONSTANTS_LIQUID_CONSTANTS["CONTENT_BLOCKS"]["ERROR_CODES"]["CONTENT_BLOCK_NOT_FOUND"]
>;
export type InvalidGrapesSupportedChannelsError = FetchUserEncodedError<
  SHARED_CONSTANTS_LIQUID_CONSTANTS["CONTENT_BLOCKS"]["ERROR_CODES"]["INVALID_GRAPES_SUPPORTED_CHANNELS"]
>;
export type InvalidGrapesJsDataError = FetchUserEncodedError<
  SHARED_CONSTANTS_LIQUID_CONSTANTS["CONTENT_BLOCKS"]["ERROR_CODES"]["INVALID_GRAPES_JS_DATA"]
>;
export type ValidationError = FetchUserEncodedError<
  SHARED_CONSTANTS_LIQUID_CONSTANTS["CONTENT_BLOCKS"]["ERROR_CODES"]["VALIDATION_ERROR"]
>;
export type TemplateParseError = FetchUserEncodedError<
  SHARED_CONSTANTS_LIQUID_CONSTANTS["CONTENT_BLOCKS"]["ERROR_CODES"]["TEMPLATE_PARSE_ERROR"]
>;
export type DuplicateContentBlockNameError = FetchUserEncodedError<
  SHARED_CONSTANTS_LIQUID_CONSTANTS["CONTENT_BLOCKS"]["ERROR_CODES"]["DUPLICATE_CONTENT_BLOCK_NAME"]
>;
export type NoPermissionsError = FetchUserEncodedError<
  SHARED_CONSTANTS_LIQUID_CONSTANTS["CONTENT_BLOCKS"]["ERROR_CODES"]["NO_PERMISSIONS"]
>;
export type ContentTooLongError = FetchUserEncodedError<
  SHARED_CONSTANTS_LIQUID_CONSTANTS["CONTENT_BLOCKS"]["ERROR_CODES"]["CONTENT_TOO_LONG"]
>;
export type InvalidContentBlockReferenceError = FetchUserEncodedError<
  SHARED_CONSTANTS_LIQUID_CONSTANTS["CONTENT_BLOCKS"]["ERROR_CODES"]["INVALID_CONTENT_BLOCK_REFERENCE"]
>;

export type ContentBlockUpsertError =
  | ContentBlockInvalidReferenceError
  | ContentBlockNotFoundError
  | InvalidGrapesSupportedChannelsError
  | InvalidGrapesJsDataError
  | ValidationError
  | TemplateParseError
  | DuplicateContentBlockNameError
  | NoPermissionsError
  | ContentTooLongError
  | InvalidContentBlockReferenceError;

export const isContentBlockUpsertError = (error: FetchRequestError): error is ContentBlockUpsertError => {
  return error instanceof FetchUserEncodedError;
};

export interface ContentBlockResult {
  info: string;
  template: {
    id: string;
    name: string;
    included_count: number;
    created: {
      time: number;
      name: string;
      d_id: string;
      api_key_name: string;
    };
    last_edited: {
      time: number;
      name: string;
      d_id: string;
      api_key_name: string;
    };
    content_type: string;
    content_editor_type: string;
    api_identifier: string;
    description: string;
    content: string;
    is_draft: boolean;
    marked_as_deleted: boolean;
    link_includer_sequence: number;
    content_block_inclusion_sequence: number;
    tag_names: string[];
    territory_ids: string[];
    dnd_json: string;
    is_dnd_template: boolean;
    grapes_supported_channels: string[];
    grapes_js_data: string;
    multi_language_composition_setup: MultiLanguageCompositionSetup | null;
  };
}

export interface ContentBlockPayload {
  id?: string;
  name: string;
  description: string;
  content: string;
  attached_images_ids: string[];
  is_draft: boolean;
  tag_names: string[];
  territory_ids: string[];
  marked_as_deleted: boolean;
  dnd_json: string;
  grapes_supported_channels: string[];
  grapes_js_data: string;
  multi_language_composition_setup: MultiLanguageCompositionSetup | null;
  link_includer_sequence_base?: number;
  link_includer_sequence_offset?: number;
  content_block_inclusion_sequence_base?: number;
  content_block_inclusion_sequence_offset?: number;
  draft_content_block_mapping?: Record<string, any>;
  links?: EmailLink[];
  tracked_link_count?: number;
}

export interface GetEngagementContentBlocksIdResult {
  id: string;
  name: string;
  included_count: number;
  // Type signature is taken from `@components/AssetUploadManager/Asset`
  attached_images?: {
    app_group_id?: string;
    id: string;
    name: string;
    url: string;
    ext: string;
    created_at: number;
    created_by?: string;
    size?: number;
    known_width?: number;
    known_height?: number;
    progress?: number;
    error?: string;
    warning?: string;
    last_edited_by?: string;
    ratio?: number;
    territory_ids?: string[];
    type?: string;
  }[];
  created: {
    time: number;
    name: string;
    d_id: string;
    api_key_name: string;
  };
  last_edited: {
    time: number;
    name: string;
    d_id: string;
    api_key_name: string;
  };
  content_type: string;
  content_editor_type: string;
  api_identifier: string;
  description: string;
  content: string;
  is_draft: boolean;
  marked_as_deleted: boolean;
  link_includer_sequence: number;
  content_block_inclusion_sequence: number;
  tag_names: string[];
  territory_ids: string[];
  dnd_json: string;
  is_dnd_template: boolean;
  grapes_supported_channels: string[];
  grapes_js_data: string;
  multi_language_composition_setup: MultiLanguageCompositionSetup | null;
  links?: any[];
}
